import { Slide, ThemeOptions } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { SnackbarProvider } from 'notistack';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Colors, SizeUnits } from '../types/interfaces';


declare module '@mui/material/styles' {
  interface CustomTheme {
    colors?:  Colors;
    fontSizes: SizeUnits;
  }
  
  interface Theme extends CustomTheme {}
  interface ThemeOptions extends CustomTheme {}
}

interface ColorSchema {
  primaryColor: string;
  secondaryColor: string;
  buttonText: string;
  buttonIcon: string;
  buttonBackground: string;
  primaryFont: string;
  secondaryFont: string;
}

interface ThemeSettingsProps {
  colors?: ColorSchema;
  children: ReactNode;
}

const ThemeSettings: FC<ThemeSettingsProps> = ({ colors, children }) => {
  const getCSSVariable = (varName: string, fallback: string): string => {
    if (typeof window !== 'undefined') {
      const value = getComputedStyle(document.documentElement)
        .getPropertyValue(varName)
        .trim();
      return value || fallback;
    }
    return fallback;
  };

  const [currentColors, setCurrentColors] = useState<ColorSchema>({
    primaryColor: getCSSVariable('--primary-color', '#3cc194'),
    secondaryColor: getCSSVariable('--secondary-color', '#1C1A1F'),
    buttonBackground: getCSSVariable('--button-background', '#3cc194'),
    buttonIcon: getCSSVariable('--button-icon', '#ffffff'),
    buttonText: getCSSVariable('--button-text', '#ffffff'),
    primaryFont: 'roboto',
    secondaryFont: 'roboto',
  });

  useEffect(() => {
    setCurrentColors(currentColors);
  }, []);

  useEffect(() => {
    setCurrentColors(currentColors);
  }, [colors]);

  const theme = (themeOptions?: ThemeOptions) => createTheme(themeOptions);

  const themeOptions = {
    palette: {
      mode: 'dark' as const,
      primary: { main: currentColors.primaryColor },
      secondary: { main: currentColors.secondaryColor },
      background: {
        default: getCSSVariable('--background-default', '#1c1a1f'),
        paper: getCSSVariable('--background-paper', '#1c1a1f'),
        primary: getCSSVariable('--background-primary', '#3cc194'),
      },
      text: {
        primary: getCSSVariable('--text-primary', '#ffffff'),
        secondary: getCSSVariable('--text-secondary', 'rgba(255, 255, 255, 0.7)'),
        disabled: getCSSVariable('--text-disabled', 'rgba(255, 255, 255, 0.3)'),
      },
      error: {
        main: getCSSVariable('--error-color', '#d32f2f'),
      },
      divider: getCSSVariable('--divider-color', 'rgba(255, 255, 255, 0.12)'),
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: `var(--button-background, ${currentColors.buttonBackground}) !important`,
              color: `var(--button-text, ${currentColors.buttonText}) !important`,
              opacity: 0.8,
            },
            '&:focus': {
              backgroundColor: `var(--button-background, ${currentColors.buttonBackground}) !important`,
              color: `var(--button-text, ${currentColors.buttonText}) !important`,
              opacity: 0.8,
            },
            '&:active': {
              backgroundColor: `var(--button-background, ${currentColors.buttonBackground}) !important`,
              color: `var(--button-text, ${currentColors.buttonText}) !important`,
              opacity: 0.9,
            },
            '&.Mui-disabled': {
              opacity: 0.5,
              backgroundColor: `var(--button-background, ${currentColors.buttonBackground}) !important`,
              color: `var(--button-text, ${currentColors.buttonText}) !important`,
            },
          },
          outlined: {
            '&:hover': {
              backgroundColor: `var(--button-background, ${currentColors.buttonBackground}) !important`,
              color: `var(--button-text, ${currentColors.buttonText}) !important`,
              borderColor: `var(--button-background, ${currentColors.buttonBackground}) !important`,
              opacity: 0.8,
            },
          },
          text: {
            '&:hover': {
              backgroundColor: `var(--button-background, ${currentColors.buttonBackground}) !important`,
              color: `var(--button-text, ${currentColors.buttonText}) !important`,
              opacity: 0.8,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-root': {
              backgroundColor: 'var(--input-background, #1c1a1f)',
              color: 'var(--input-text, #ffffff)',
            },
            '& .MuiInputBase-input': {
              color: 'var(--input-text, #ffffff)',
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'var(--input-placeholder, rgba(255, 255, 255, 0.5))',
              opacity: 1,
            },
            '& .MuiInputLabel-root': {
              color: 'var(--input-label, rgba(255, 255, 255, 0.7))',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'var(--input-border, #3cc194)',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--input-background, #1c1a1f)',
            color: 'var(--input-text, #ffffff)',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--input-border, rgba(255, 255, 255, 0.23))',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--input-border-hover, rgba(255, 255, 255, 0.5))',
            },
            // Regular inputs - consistent border (not primary), but preserve error states
            '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
              borderWidth: '2px',
            },
            // Error state should still work - use theme error color
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--error-color, #d32f2f)',
            },
            '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--error-color, #d32f2f)',
            },
            '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--error-color, #d32f2f)',
              borderWidth: '2px',
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              color: 'rgba(255, 255, 255, 0.3)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.1)',
              },
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--input-border, #59585a)',
            },
            '& .MuiOutlinedInput-root:not(.Mui-error):hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--primary-color, #3cc194)',
            },
            '& .MuiOutlinedInput-root:not(.Mui-error).Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--primary-color, #3cc194)',
              borderWidth: '2px',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'var(--primary-color, #3cc194)',
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--input-background, #1c1a1f)',
            color: 'var(--input-text, #ffffff)',
            '& .MuiSelect-icon': {
              color: 'var(--input-text, #ffffff)',
            },
            '& .MuiOutlinedInput-root:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--primary-color, #3cc194)',
            },
            '& .MuiOutlinedInput-root:not(.Mui-error):hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--primary-color, #3cc194)',
            },
            '& .MuiOutlinedInput-root:not(.Mui-error).Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--primary-color, #3cc194)',
              borderWidth: '2px',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'var(--primary-color, #3cc194)',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: 'var(--input-label, rgba(255, 255, 255, 0.7))',
            // Regular input labels - consistent color (not primary), but preserve error states
            '&.Mui-focused:not(.Mui-error)': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
            // Error state should show error color
            '&.Mui-error': {
              color: 'var(--error-color, #d32f2f)',
            },
            '&.Mui-error.Mui-focused': {
              color: 'var(--error-color, #d32f2f)',
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: 'var(--input-label, rgba(255, 255, 255, 0.7))',
            '&.Mui-focused': {
              color: 'var(--input-border, #3cc194)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--background-paper, #1c1a1f)',
            color: 'var(--text-primary, #ffffff)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--background-paper, #1c1a1f)',
            color: 'var(--text-primary, #ffffff)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: 'var(--dialog-background, #1c1a1f)',
            color: 'var(--text-primary, #ffffff)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: 'var(--drawer-background, #1c1a1f)',
            color: 'var(--text-primary, #ffffff)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--appbar-background, #1c1a1f)',
            color: 'var(--text-primary, #ffffff)',
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--background-primary, #1c1a1f)',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--background-paper, #1C1A1F)',
            position: 'sticky' as const,
            top: 0,
            zIndex: 1000,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: 'var(--border-default, rgba(255, 255, 255, 0.12))',
            color: 'var(--text-primary, #ffffff)',
          },
          head: {
            backgroundColor: 'var(--background-paper, #1C1A1F)',
            color: 'var(--text-primary, #ffffff)',
            fontWeight: 600,
            position: 'sticky' as const,
            top: 0,
            zIndex: 1001,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: 'var(--table-row-hover, rgba(255, 255, 255, 0.05))',
            },
            '&.Mui-selected': {
              backgroundColor: 'var(--table-row-hover, rgba(255, 255, 255, 0.08))',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--chip-background, rgba(255, 255, 255, 0.1))',
            color: 'var(--chip-text, #ffffff)',
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: 'var(--text-primary, #ffffff)',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: 'var(--menu-background, #1c1a1f)',
            color: 'var(--text-primary, #ffffff)',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: 'var(--menu-item-hover, rgba(255, 255, 255, 0.08))',
            },
            '&.Mui-selected': {
              backgroundColor: 'var(--menu-item-hover, rgba(255, 255, 255, 0.12))',
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: 'var(--tooltip-background, rgba(0, 0, 0, 0.9))',
            color: 'var(--tooltip-text, #ffffff)',
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: 'var(--divider-color, rgba(255, 255, 255, 0.12))',
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--background-paper, #1c1a1f)',
            color: 'var(--text-primary, #ffffff)',
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: 'var(--menu-item-hover, rgba(255, 255, 255, 0.08))',
            },
            '&.Mui-selected': {
              backgroundColor: 'var(--menu-item-hover, rgba(255, 255, 255, 0.12))',
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: 'var(--text-secondary, rgba(255, 255, 255, 0.7))',
            '&.Mui-checked': {
              color: 'var(--primary-color, #3cc194)',
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: 'var(--text-secondary, rgba(255, 255, 255, 0.7))',
            '&.Mui-checked': {
              color: 'var(--primary-color, #3cc194)',
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: 'var(--primary-color, #3cc194)',
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: 'var(--primary-color, #3cc194)',
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            borderBottom: '1px solid var(--border-default, rgba(255, 255, 255, 0.12))',
          },
          indicator: {
            backgroundColor: 'var(--primary-color, #3cc194)',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: 'var(--text-secondary, rgba(255, 255, 255, 0.7))',
            '&.Mui-selected': {
              color: 'var(--text-primary, #ffffff)',
            },
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--background-paper, #1c1a1f)',
            color: 'var(--text-primary, #ffffff)',
            '&:before': {
              backgroundColor: 'var(--border-default, rgba(255, 255, 255, 0.12))',
            },
          },
        },
      },
      MuiPagination: {
        styleOverrides: {
          root: {
            '& .MuiPaginationItem-root': {
              color: 'var(--text-primary, #ffffff)',
            },
            '& .MuiPaginationItem-root.Mui-selected': {
              backgroundColor: 'var(--primary-color, #3cc194)',
              color: 'var(--button-text, #ffffff)',
            },
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--background-paper, #1c1a1f)',
            color: 'var(--text-primary, #ffffff)',
            borderColor: 'var(--border-default, rgba(255, 255, 255, 0.12))',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'var(--background-paper, #1C1A1F)',
              color: 'var(--text-primary, #ffffff)',
              position: 'sticky' as const,
              top: 0,
              zIndex: 1000,
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: 'var(--background-paper, #1C1A1F)',
              position: 'sticky' as const,
              top: 0,
              zIndex: 1001,
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                backgroundColor: 'var(--table-row-hover, rgba(255, 255, 255, 0.05))',
              },
              '&.Mui-selected': {
                backgroundColor: 'var(--table-row-hover, rgba(255, 255, 255, 0.08))',
              },
            },
            '& .MuiDataGrid-cell': {
              borderColor: 'var(--border-default, rgba(255, 255, 255, 0.12))',
              color: 'var(--text-primary, #ffffff)',
            },
            '& .MuiDataGrid-footerContainer': {
              borderColor: 'var(--border-default, rgba(255, 255, 255, 0.12))',
            },
          },
        },
      },
      MuiPickersCalendarHeader: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--background-paper, #1c1a1f)',
            color: 'var(--text-primary, #ffffff)',
          },
        },
      },
      MuiDateCalendar: {
        defaultProps: {
          disableHighlightToday: true,
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            color: 'var(--text-primary, #ffffff)',
            '&.Mui-selected': {
              backgroundColor: 'var(--primary-color, #3cc194)',
              color: 'var(--button-text, #ffffff)',
            },
            '&:hover': {
              backgroundColor: 'var(--menu-item-hover, rgba(255, 255, 255, 0.08))',
            },
            // Remove green/today circle – today is not highlighted (including when disabled)
            '&.MuiPickersDay-today:not(.Mui-selected)': {
              border: 'none',
              backgroundColor: 'transparent',
              '&:focus, &:hover': {
                backgroundColor: 'transparent',
              },
              '& .MuiTouchRipple-root': {
                display: 'none',
              },
            },
            // Remove green circle on disabled days (including disabled + today)
            '&.MuiPickersDay-disabled:not(.Mui-selected)': {
              '&:focus, &:hover': {
                backgroundColor: 'transparent',
              },
              '& .MuiTouchRipple-root': {
                display: 'none',
              },
            },
          },
        },
      },
      MuiPickersPopper: {
        styleOverrides: {
          paper: {
            backgroundColor: 'var(--background-paper, #1c1a1f)',
            color: 'var(--text-primary, #ffffff)',
          },
        },
      },
      MuiPickersTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root:not(.Mui-error)': {
              '& fieldset': {
                borderColor: 'var(--primary-color, #3cc194)',
              },
              '&:hover fieldset': {
                borderColor: 'var(--primary-color, #3cc194)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'var(--primary-color, #3cc194)',
                borderWidth: 2,
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'var(--primary-color, #3cc194)',
            },
            /* Remove green circle from calendar icon – transparent, neutral hover */
            '& .MuiIconButton-root': {
              backgroundColor: 'transparent',
              color: 'var(--text-secondary, rgba(255, 255, 255, 0.7))',
            },
            '& .MuiIconButton-root:hover, & .MuiIconButton-root.Mui-focusVisible': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
          },
        },
      },
      MuiPickersInputBase: {
        styleOverrides: {
          root: {
            '& .MuiIconButton-root': {
              backgroundColor: 'transparent',
              color: 'var(--text-secondary, rgba(255, 255, 255, 0.7))',
            },
            '& .MuiIconButton-root:hover, & .MuiIconButton-root.Mui-focusVisible': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
          },
        },
      },
    },
    overrides: {
      MuiStepIcon: {
        root: {
          '&$completed': {
            color: 'pink',
          },
          '&$active': {
            color: 'red',
          },
        },
        active: {},
        completed: {},
      },
    },
    colors: {
      primary: {
        '5': `${currentColors?.primaryColor}05`,
        '10': `${currentColors?.primaryColor}10`,
        '20': `${currentColors?.primaryColor}20`,
        '40': `${currentColors?.primaryColor}40`,
        '60': `${currentColors?.primaryColor}60`,
        '80': `${currentColors?.primaryColor}80`,
        default: `${currentColors?.primaryColor}`,
      },
      secondary: {
        '5': `${currentColors?.secondaryColor}05`,
        '10': `${currentColors?.secondaryColor}10`,
        '20': `${currentColors?.secondaryColor}20`,
        '40': `${currentColors?.secondaryColor}40`,
        '60': `${currentColors?.secondaryColor}60`,
        '80': `${currentColors?.secondaryColor}80`,
        default: `${currentColors?.secondaryColor}`,
      },
      buttonText: {
        '5': `${currentColors?.buttonText}05`,
        '10': `${currentColors?.buttonText}10`,
        '20': `${currentColors?.buttonText}20`,
        '40': `${currentColors?.buttonText}40`,
        '60': `${currentColors?.buttonText}60`,
        '80': `${currentColors?.buttonText}80`,
        default: `${currentColors?.buttonText}`,
      },
      buttonIcon: {
        '5': `${currentColors?.buttonIcon}05`,
        '10': `${currentColors?.buttonIcon}10`,
        '20': `${currentColors?.buttonIcon}20`,
        '40': `${currentColors?.buttonIcon}40`,
        '60': `${currentColors?.buttonIcon}60`,
        '80': `${currentColors?.buttonIcon}80`,
        default: `${currentColors?.buttonIcon}`,
      },
      buttonBackground: {
        '5': `${currentColors?.buttonBackground}05`,
        '10': `${currentColors?.buttonBackground}10`,
        '20': `${currentColors?.buttonBackground}20`,
        '40': `${currentColors?.buttonBackground}40`,
        '60': `${currentColors?.buttonBackground}60`,
        '80': `${currentColors?.buttonBackground}80`,
        default: `${currentColors?.buttonBackground}`,
      },
      photoBlue: {
        '5': '#C1E0E705',
        '10': '#C1E0E710',
        '20': '#C1E0E720',
        '40': '#C1E0E740',
        '60': '#C1E0E760',
        '80': '#C1E0E780',
        default: '#C1E0E7',
      },
      deepSteelGrey: {
        '5': '#3B3E425',
        '10': '#3B3E4210',
        '20': '#3B3E4220',
        '40': '#3B3E4240',
        '60': '#3B3E4260',
        '80': '#3B3E4280',
        default: '#3B3E42',
      },
      lightStaleGrey: {
        '5': '#77889905',
        '10': '#77889910',
        '20': '#77889920',
        '40': '#77889940',
        '60': '#77889960',
        '80': '#77889980',
        default: '#778899',
      },
      aliceBlue: {
        '5': '#F0F8FF05',
        '10': '#F0F8FF10',
        '20': '#F0F8FF20',
        '40': '#F0F8FF40',
        '60': '#F0F8FF60',
        '80': '#F0F8FF80',
        default: '#F0F8FF',
      },
      lightGrey: {
        '5': '#D3D3D305',
        '10': '#D3D3D310',
        '20': '#D3D3D320',
        '40': '#D3D3D340',
        '60': '#D3D3D360',
        '80': '#D3D3D380',
        default: '#D3D3D3',
      },
      almond: {
        '5': '#EFDECD05',
        '10': '#EFDECD10',
        '20': '#EFDECD20',
        '40': '#EFDECD40',
        '60': '#EFDECD60',
        '80': '#EFDECD80',
        default: '#EFDECD',
      },
      davyGrey: {
        '5': '#55555505',
        '10': '#55555510',
        '20': '#55555520',
        '40': '#55555540',
        '60': '#55555560',
        '80': '#55555580',
        default: '#555555',
      },
      onyx: {
        '5': '#35383905',
        '10': '#35383910',
        '20': '#35383920',
        '40': '#35383940',
        '60': '#35383960',
        '80': '#35383980',
        default: '#353839',
      },
      charcoal: {
        '5': '#36454F05',
        '10': '#36454F10',
        '20': '#36454F20',
        '40': '#36454F40',
        '60': '#36454F60',
        '80': '#36454F80',
        default: '#36454F',
      },
      cadet: {
        '5': '#53687205',
        '10': '#53687210',
        '20': '#53687220',
        '40': '#53687240',
        '60': '#53687260',
        '80': '#53687280',
        default: '#536872',
      },
      white: {
        default: '#FFFFFF',
      },
      black: {
        default: '#000000',
      },
      error: {
        '5': '#E32B2405',
        '10': '#E32B2410',
        '20': '#E32B2420',
        '40': '#E32B2440',
        '60': '#E32B2460',
        '80': '#E32B2480',
        default: '#E32B24',
      },
      warning: {
        '5': '#FFC10705',
        '10': '#FFC10710',
        '20': '#FFC10720',
        '40': '#FFC10740',
        '60': '#FFC10760',
        '80': '#FFC10780',
        default: '#FFC107',
      },
      success: {
        '5': '#18BB0C05',
        '10': '#18BB0C10',
        '20': '#18BB0C20',
        '40': '#18BB0C40',
        '60': '#18BB0C60',
        '80': '#18BB0C80',
        default: '#18BB0C',
      },
      lightBlue: {
        '5': '#2680EB5',
        '10': '#2680EB10',
        '20': '#2680EB20',
        '40': '#2680EB40',
        '60': '#2680EB60',
        '80': '#2680EB80',
        default: '#2680EB',
      },
    },
    fontSizes: {
      h1: '34px',
      h2: '22px',
      h3: '20px',
      xl: '18px',
      large: '16px',
      medium: '14px',
      small: '12px',
    },
  };

  return (
    <ThemeProvider theme={theme(themeOptions)}>
      <SnackbarProvider
        maxSnack={1}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        TransitionComponent={Slide}
        autoHideDuration={3000}
      >
        {children}
      </SnackbarProvider>
    </ThemeProvider>
  );
};

export default ThemeSettings;
